'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Autocomplete, type AutocompleteOption } from '@kit/ui/autocomplete';

import { getAllCountryOptions, type CountryOption } from '../../lib/countries';

interface CountrySelectorProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Optional custom country list. If not provided, uses complete ISO 3166-1 list */
  countries?: Array<{ country_code: string; country_name: string }>;
  disabled?: boolean;
  /**
   * Optional context slot rendered above the search field (desktop) and above
   * the sheet search input (mobile). Forwarded verbatim to the primitive.
   */
  header?: ReactNode;
  /**
   * Trigger placeholder shown when nothing is selected. When omitted, falls
   * back to the `common:selectCountry` translation — supply an explicit string
   * on surfaces that don't load the `dancers`/`common` namespaces.
   */
  placeholder?: string;
  /**
   * Search-input placeholder. When omitted, falls back to the
   * `dancers:profileSetup.searchCity` translation.
   */
  searchPlaceholder?: string;
  /**
   * Validation error message. Forwarded to the underlying `Autocomplete` so the
   * field renders its error-border affordance (parity with the previous
   * `Select`-based field that turned red on error).
   */
  error?: string;
}

/**
 * Single-country picker. Thin wrapper over the canonical `@kit/ui/autocomplete`
 * primitive (type-to-filter inline, no separate search box), sourcing the full
 * ISO 3166-1 list (+ flags) from `getAllCountryOptions`.
 *
 * Canonical home (`@kit/artists`) — the package that owns the country dataset.
 * Consumed by the dancer profile wizard, contract details, and the competition
 * application form.
 */
export function CountrySelector({
  value,
  onValueChange,
  countries: customCountries,
  disabled = false,
  placeholder,
  searchPlaceholder,
  error,
  header,
}: CountrySelectorProps) {
  const { t } = useTranslation('dancers');

  // Use complete country list if no custom list provided
  const options = useMemo<AutocompleteOption[]>(() => {
    const source =
      customCountries && customCountries.length > 0
        ? customCountries
        : getAllCountryOptions();
    return source.map((country) => {
      const flag =
        'flag' in country ? (country as CountryOption).flag : undefined;
      return {
        value: country.country_code,
        label: `${country.country_name} (${country.country_code})`,
        icon: flag ? <span>{flag}</span> : undefined,
      };
    });
  }, [customCountries]);

  return (
    <Autocomplete
      // Option values are uppercase ISO codes; normalize the incoming value so a
      // legacy lowercase-stored code ("us") still resolves to its option ("US")
      // instead of rendering blank. Re-saving then persists the canonical code.
      value={value ? value.toUpperCase() : undefined}
      onChange={(next) => onValueChange(typeof next === 'string' ? next : '')}
      options={options}
      disabled={disabled}
      header={header}
      // Rich collapsed summary reuses the dropdown row visuals (flag + name),
      // so the trigger reads identically to the option the user picked.
      renderSelected={(option) =>
        option ? (
          <span className="flex items-center gap-2">
            {option.icon}
            <span>{option.label}</span>
          </span>
        ) : null
      }
      placeholder={placeholder ?? t('common:selectCountry')}
      emptyMessage={t('common:noResultsFound')}
      aria-label={searchPlaceholder ?? t('common:selectCountry')}
      error={error}
    />
  );
}
